import { FC, useCallback } from 'react';

import { PlayerApiError } from '@/scenarios/legacy-player/types/error';
import { useOnContentImpression } from '@/scenarios/legacy-player/hooks/useOnContentImpression';
import { useOnFinish } from '@/scenarios/legacy-player/hooks/useOnFinish';
import { useOnReady } from '@/scenarios/legacy-player/hooks/useOnReady';
import { useOnSourceChanged } from '@/scenarios/legacy-player/hooks/useOnSourceChanged';
import { useOnWatchedTimeChanged } from '@/scenarios/legacy-player/hooks/useOnWatchedTimeChanged';
import { useSetNativeSubtitlesVisibility } from '@/scenarios/legacy-player/hooks/useSetNativeSubtitlesVisibility';
import { useTogglePlay } from '@/scenarios/legacy-player/hooks/useTogglePlay';
import { ReadonlySource } from '@/scenarios/legacy-player/types/config';

import { useVodActiveTabController } from '../hooks/use-vod-active-tab-controller';
import { useOnError } from '../../feature-player/hooks/controller/use-on-error';
import { useSoundPreferencesWatcher } from '../../feature-player/hooks/config/use-sound-preferences';
import { useSaveTimings } from '../../movie-player/hooks/use-save-timings';
import { hidePlayerIframeToScreenReaders } from '../../feature-player/utils/hide-player-iframe-to-screen-readers';
import { useErrorInfo } from '../../feature-player/hooks/player-instance/use-error-info';

export interface VodPlayerControllerProps {
    contentId: string;
    tvSeriesId?: string;
    timingsInterval?: number;
    videoToken?: string;
    onSourceChanged?: (newSource: ReadonlySource | undefined) => void;
    onContentImpression: () => void;
    onFinish?: () => void;
    onError?: (error: PlayerApiError) => void;
    onWatchedTimeChanged: (watchedTime: number) => void;
}

export const VodPlayerController: FC<VodPlayerControllerProps> = ({
    contentId,
    tvSeriesId,
    timingsInterval,
    videoToken,
    onSourceChanged,
    onContentImpression,
    onFinish,
    onError,
    onWatchedTimeChanged,
}) => {
    const { play } = useTogglePlay();
    const onSourceChangedCallback = useCallback(
        (source: ReadonlySource | undefined) => {
            onSourceChanged?.(source);
            play();
        },
        [onSourceChanged, play]
    );

    useOnSourceChanged(onSourceChangedCallback);
    useOnContentImpression(onContentImpression);
    useOnError(onError);
    useOnFinish(onFinish);
    useOnWatchedTimeChanged(onWatchedTimeChanged);
    useErrorInfo();
    useOnReady(hidePlayerIframeToScreenReaders);
    useVodActiveTabController();

    useSetNativeSubtitlesVisibility(false);
    useSaveTimings({
        contentId,
        tvSeriesId: contentId !== tvSeriesId ? tvSeriesId : undefined,
        timingsInterval,
        videoToken,
    });

    useSoundPreferencesWatcher();

    return null;
};
